// The section a page's title names, shared by the server-rendered head
// (`server-seo.ts`) and the SPA's `document.title` on every navigation. Both
// compose it with `composeTitle` from `seo-text.ts`. Two tables would drift,
// and GA4 records whichever string the tab carries: a page whose first load
// and in-app arrival disagree reports as two pages.
//
// Section subjects are English on purpose, whatever the viewer's locale. The
// title is an analytics dimension as much as a tab label, and a localised one
// would split every page into one row per language.

import circlesCopy from '../../messages/en/circles.json';
import invitationsCopy from '../../messages/en/invitations.json';
import legalCopy from '../../messages/en/legal.json';
import networksCopy from '../../messages/en/networks.json';
import notificationsCopy from '../../messages/en/notifications.json';
import resourcesCopy from '../../messages/en/resources.json';
import searchCopy from '../../messages/en/search.json';
import shellCopy from '../../messages/en/shell.json';

// Every subject here is product copy, never member-authored text. The title
// reaches GA4 on every event (gtag reads `document.title`), and Google's
// terms forbid sending it anything that could identify a person, so a page
// about a member or a member's post is titled by its section, not its
// subject. Entity names reach the title only through a route loader's
// `pageTitle`, and only on the editorial routes that return one.
//
// Matched by whole path segment, so `/join` does not claim `/join-a-circle`.
const SECTION_SUBJECTS: ReadonlyArray<readonly [prefix: string, subject: string]> = [
  ['/admin', shellCopy.nav.adminPanel],
  ['/circles', circlesCopy.chrome.title],
  ['/coach', shellCopy.nav.careerCoach],
  ['/community-guidelines', 'Community Guidelines'],
  ['/events', shellCopy.nav.events],
  ['/feed', shellCopy.nav.home],
  ['/groups', shellCopy.nav.groups],
  ['/help', shellCopy.nav.help],
  ['/invitations', invitationsCopy.page.title],
  ['/join-a-circle', 'Join a Circle'],
  ['/join-a-network', 'Join a Network'],
  ['/join', 'Join Lean In'],
  ['/members', 'Members'],
  ['/messages', shellCopy.nav.messages],
  ['/moderation', shellCopy.nav.moderation],
  ['/networks', networksCopy.chrome.title],
  ['/notifications', notificationsCopy.title],
  ['/onboarding', 'Onboarding'],
  ['/people', shellCopy.nav.people],
  ['/posts', 'Posts'],
  ['/privacy', legalCopy.privacy.title],
  ['/profile', 'Profile'],
  ['/resources', resourcesCopy.list.title],
  ['/roadmap', shellCopy.nav.roadmap],
  ['/search', searchCopy.results.title],
  ['/settings', shellCopy.nav.settings],
  ['/start-a-circle', 'Start a Circle'],
  ['/terms', legalCopy.terms.title],
];

// The section a path belongs to, or null for a path that is only the brand
// (the home page, auth, invite links).
export function sectionSubject(pathname: string): string | null {
  const match = SECTION_SUBJECTS.find(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  return match === undefined ? null : match[1];
}
